/* eslint @typescript-eslint/naming-convention:0 */
import {
  Confirm,
  type FieldProps,
  Form,
  FormDataConsumer,
  type Identifier,
  type InputProps,
  Labeled,
  Loading,
  NotFound,
  SelectInput,
  SimpleShowLayout,
  useCreate,
  useDataProvider,
  useGetOne,
  useInput,
  useNotify,
  useRedirect,
  useTranslate,
  useUpdate
} from 'react-admin'
import {
  useForm,
  useFormContext,
  useFormState,
  useWatch
} from 'react-hook-form'
import React, { useState } from 'react'
import {
  Box,
  Button,
  type ButtonProps,
  FormControlLabel,
  Grid,
  Popover,
  Radio,
  Typography,
  useMediaQuery
} from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import log from 'loglevel'
import {
  includes,
  isEmpty,
  isNil,
  join,
  last,
  map,
  omit,
  reduce,
  replace,
  size,
  slice,
  split,
  startCase,
  template,
  toNumber,
  toString
} from 'lodash/fp'
import axios, { type AxiosResponse } from 'axios'
import { zodResolver } from '@hookform/resolvers/zod'
import { parsePhoneNumber } from 'libphonenumber-js'
import { useParams, useSearchParams } from 'react-router-dom'
import { format, parseISO, subDays, subMonths } from 'date-fns/fp'

import {
  YesNoInput,
  DateInput,
  TextInput,
  CheckInput,
  RadioInput,
  ValidateError
} from 'components/Util'
import {
  userSchemasWithContractor,
  userSchemasWithContractorNoFamily,
  userSchemasWithoutContractor,
  userSchemasWithoutContractorNoFamily,
  userSchemaAll,
  familySchema,
  paymentSchema
} from './schema'
import {
  type EntryPartner,
  type EntryUserData
} from '../../../../cdk/bapp/lib/lambda-api/src/types'
import ekyc from './ekyc.json'
import { hankana2Zenkana } from '../Util/functions'
import { assign } from 'lodash'
import { z } from 'zod'

log.setLevel(log.levels.DEBUG)

type PostalCodeApiResult = {
  code: string
  data: [
    {
      prefcode: string
      ja: {
        prefecture: string
        address1: string
        address2: string
        address3: string
        address4: string
      }
      en: {
        prefecture: string
        address1: string
        address2: string
        address3: string
        address4: string
      }
    }
  ]
}

const env = process.env['ENV'] as string
const local = process.env['LOCAL'] as string
const { API_ENDPOINT, KEY } = ekyc[env]
const disableDirectDebit = !isNil(process.env['DISABLE_DIRECT_DEBIT'])
const formDefaultValue = !isNil(process.env['INPUT_TEST'])
  ? {
      address: '愛知県名古屋市中区栄4-5-3',
      contractor: {
        name: 'てすとなまえ',
        lastName: 'てすと',
        firstName: 'なまえ',
        nameKana: 'テストナマエ',
        lastNameKana: 'テスト',
        firstNameKana: 'ナマエ',
        postalCode: '4600008',
        address: '愛知県名古屋市中区栄4-5-3',
        phone: '09012345678',
        email: 'otokawa.hideyuki@miraiz-connect.co.jp'
      },
      country: 'JP',
      entry: '2024-08-01',
      // necessaryContractor: false,
      // necessaryEmail: true
      // necessaryEKYC: false,
      families: [
        isNil(process.env['NO_FAMILY'])
          ? {
              name: 'てすとなまえ',
              lastName: 'てすと',
              firstName: 'なまえ',
              nameKana: 'テストナマエ',
              lastNameKana: 'テスト',
              firstNameKana: 'ナマエ',
              postalCode: '4600008',
              phone: '09012345678',
              email: 'otokawa.hideyuki@miraiz-connect.co.jp',
              country: 'JP'
            }
          : {
              // name: '  ',
              // lastName: ' ',
              // firstName: ' ',
              // phone: ''
            }
      ],
      name: '中電太郎',
      lastName: '中電',
      firstName: '太郎',
      nameKana: 'チュウデンタロウ',
      lastNameKana: 'チュウデン',
      firstNameKana: 'タロウ',
      phone: '09012345678',
      place: 'KDX名古屋ビル',
      placeNumber: '100',
      postalCode: '4600008',
      // lessee: {
      //   name: 'テスト'
      // },
      email: 'otokawa.hideyuki@miraiz-connect.co.jp',
      agreement: false,
      attributes: [
        env === 'exp'
          ? { key: 'ID1', value: '12345' }
          : { key: '共通ID', value: '12345' },
        env === 'exp'
          ? { key: 'ID1', value: '12345' }
          : { key: '共通ID', value: '12345' }
      ]
    }
  : {}

const postalCodeApi = 'https://madefor.github.io/postal-code-api/api/v1/'

const getEkycUrl = async (
  user: EntryUserData & { id: string }
): Promise<string | undefined> => {
  const { id, lastName, firstName, address } = user
  const param = {
    applicant_id: replace(/[-_]/g, '')(id),
    id_document_types: ['15001', '15002', '15003', '15004', '15005', '15005'],
    last_name: lastName,
    first_name: firstName,
    birthday: format('yyyyMMdd')(subDays(1)(Date.now())),
    address1: address
  }
  const liquidEkyc = window.LIQUIDEKYC
  liquidEkyc.API_ENDPOINT = API_ENDPOINT
  liquidEkyc.KEY = KEY
  const { application_url } = await liquidEkyc
    .startKYC(param)
    .catch(({ error_code, error_message }) => {
      log.error('[OrderFormR.getEkycUrl] error', { error_code, error_message })
      return { application_url: undefined }
    })
  return application_url
}
const EKYCButton: React.FC<{ user: EntryUserData & { id: string } }> = ({
  user
}) => {
  const translate = useTranslate()
  const onClick = (): void => {
    const ref = window.open()
    getEkycUrl(user)
      .then((url: string | undefined) => {
        if (!isNil(url) && !isNil(ref)) {
          ref.location = url
        }
      })
      .catch(log.error)
  }
  return (
    <>
      <Grid container justifyContent="center">
        <Grid item xs={3} sx={{ ml: '0.5em' }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{ borderRadius: '10px' }}
            fullWidth
            // disabled={isNil(url)}
            onClick={onClick}>
            <Typography variant="button">
              {translate('resources.orderR.buttons.ekyc')}
            </Typography>
          </Button>
        </Grid>
      </Grid>
    </>
  )
}
const SubmitButton: React.FC<{
  product: string
  page: number
  config: EntryPartner
  schema: any
}> = ({ product, page, config, schema }) => {
  const [open, setOpen] = useState(false)
  const [enable, setEnable] = useState(false)
  const { getValues } = useFormContext()
  const { isValid } = useFormState()
  const translate = useTranslate()
  const redirect = useRedirect()
  const [create] = useCreate<EntryUserData, Error>(undefined, undefined, {
    onError: (error: Error) => {
      log.error('[OrderFormR.SubmitButton.create]', { error })
    },
    onSettled: (
      data: (EntryUserData & { id: Identifier }) | undefined,
      error: Error | null
    ) => {
      if (!isNil(error) || isNil(data)) {
        log.error('[OrderFormR.SubmitButton.create]', { error })
        redirect(`/${product}/error`)
        return
      }
      const { id } = data
      redirect(`/${product}/complete/${id}`)
    }
  })
  const [update] = useUpdate<EntryUserData & { id: string }, Error>(
    undefined,
    undefined,
    {
      onError: (error: Error) => {
        log.error('[OrderFormR.SubmitButton.update]', { error })
      },
      onSettled: (
        data: (EntryUserData & { id: Identifier }) | undefined,
        error: Error | null
      ) => {
        if (!isNil(error) || isNil(data)) {
          log.error('[OrderFormR.SubmitButton.update]', { error })
          redirect(`/${product}/error`)
          return
        }
        const { id } = data
        redirect(`/${product}/complete/${id}`)
      }
    }
  )
  const onClick = (): void => {
    setOpen(true)
  }
  const onClose = (): void => {
    setOpen(false)
  }
  const onConfirm = (): void => {
    setOpen(false)
    setEnable(false)
    const user = getValues() as EntryUserData & {
      id?: string
      noFamilies: boolean
    }
    const partner = join('-')(slice(0, 5)(split('-')(product)))
    const { id, contractor, necessaryContractor, noFamilies } = user
    const data = {
      ...omit(['agreementDisabled'])(user),
      contractor: necessaryContractor ? contractor : undefined,
      partner,
      product
    }
    if (noFamilies) {
      assign(data, {
        families: [
          {
            name: ' ',
            lastName: ' ',
            firstName: ' ',
            nameKana: ' ',
            lastNameKana: ' ',
            firstNameKana: ' ',
            postalCode: '',
            phone: '',
            country: 'JP'
          }
        ]
      })
    }
    // @ts-expect-error ts(2339)
    const attributes = map.convert({ cap: false })(
      (_item: any, index: number) => {
        const { key, value } = data[`attributes${index}`]
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete data[`attributes${index}`]
        return { key, value }
      }
    )(config.attributes)
    if (!isEmpty(attributes)) assign(data, { attributes })
    if (isNil(id)) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      create('users', { data })
    } else {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      update('users', { id, data })
    }
  }
  React.useEffect(() => {
    setEnable(schema.safeParse(getValues()).success)
  }, [isValid, schema])
  return (
    <>
      <Grid container justifyContent="center">
        <Grid item xs={3}>
          <BackButton product={product} page={page - 1} />
        </Grid>
        <Grid item xs={3} sx={{ ml: '0.5em' }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{ borderRadius: '10px' }}
            disabled={!enable}
            fullWidth
            onClick={onClick}>
            <Typography variant="button">
              {translate('resources.orderR.buttons.submit')}
            </Typography>
          </Button>
        </Grid>
      </Grid>
      <Confirm
        isOpen={open}
        title={false}
        content="resources.orderR.confirms.submit.content"
        confirm="resources.orderR.confirms.submit.confirm"
        onConfirm={onConfirm}
        onClose={onClose}
      />
    </>
  )
}
const ConfirmButton: React.FC<{
  product: string
  page: number
  schema: any
}> = ({ product, page, schema }) => {
  const [enable, setEnable] = useState(false)
  const { isValid } = useFormState()
  const translate = useTranslate()
  const { getValues } = useFormContext()
  const { watch } = useForm()
  const redirect = useRedirect()
  const onClick = (): void => {
    setEnable(false)
    redirect(`/${product}/${page + 1}`)
  }
  const agreement = watch('agreement')
  const parsed = schema.safeParse(getValues())
  React.useEffect(() => {
    setEnable(parsed.success)
    if (!isNil(parsed.error)) log.debug({ error: parsed.error.issues })
  }, [isValid, schema, agreement, parsed])
  return (
    <Grid container justifyContent="center">
      <Grid item xs={3}>
        <BackButton product={product} page={page - 1} />
      </Grid>
      <Grid item xs={3} sx={{ ml: '0.5em' }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          sx={{ borderRadius: '10px' }}
          disabled={!enable}
          fullWidth
          onClick={onClick}>
          <Typography variant="button">
            {translate('resources.orderR.buttons.confirm')}
          </Typography>
        </Button>
      </Grid>
    </Grid>
  )
}
const BackButton: React.FC<{ product: string; page: number }> = ({
  product,
  page
}) => {
  const translate = useTranslate()
  const redirect = useRedirect()
  const onClick = (): void => {
    redirect(`/${product}/${page}`)
  }
  return (
    <Button
      variant="contained"
      color="primary"
      size="large"
      sx={{ borderRadius: '10px' }}
      fullWidth
      onClick={onClick}>
      <Typography variant="button">
        {translate('resources.orderR.buttons.back')}
      </Typography>
    </Button>
  )
}
const NextButton: React.FC<{
  product: string
  page: number
  schema: any
}> = ({ product, page, schema }) => {
  const [enable, setEnable] = useState(false)
  const { isValid } = useFormState()
  const { getValues } = useFormContext()
  const translate = useTranslate()
  const redirect = useRedirect()
  const onClick = (): void => {
    setEnable(false)
    redirect(`/${product}/${page + 1}`)
  }
  const parsed = schema.safeParse(getValues())
  React.useEffect(() => {
    setEnable(parsed.success)
    if (!isNil(parsed.error)) log.debug({ error: parsed.error.issues })
  }, [isValid, schema, parsed])
  return (
    <Grid container justifyContent="center">
      {page > 0 && (
        <Grid item xs={3}>
          <BackButton product={product} page={page - 1} />
        </Grid>
      )}
      <Grid item xs={3} sx={{ ml: '0.5em' }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          sx={{ borderRadius: '10px' }}
          disabled={!enable}
          fullWidth
          onClick={onClick}>
          <Typography variant="button">
            {translate('resources.orderR.buttons.next')}
          </Typography>
        </Button>
      </Grid>
    </Grid>
  )
}
const CountryInput: React.FC<InputProps & { fields: string }> = ({
  fields,
  ...props
}) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const { source } = props
  const translate = useTranslate()
  return (
    <>
      <Typography variant="subtitle1">
        {translate(`${fields}.${replace(/\.\d/, '')(source)}`)}
      </Typography>
      <SelectInput
        {...props}
        label={false}
        choices={[
          { id: 'JP', name: '日本語' },
          { id: 'EN', name: '英語' }
        ]}
        emptyValue={'JP'}
        defaultValue={'JP'}
        fullWidth
        sx={{
          paddingLeft: isDesktop ? '0.5em' : '0.25em',
          paddingRight: isDesktop ? '0.5em' : '0.25em'
        }}
      />
    </>
  )
}

const AddressInputButton: React.FC<
  ButtonProps & { field: string; postalCode: string | null | undefined }
> = ({ field, postalCode, ...props }) => {
  const [disabled, setDisabled] = React.useState(true)
  const translate = useTranslate()
  const notify = useNotify()
  const { setValue } = useFormContext()
  React.useEffect(() => {
    setDisabled(size(postalCode) !== 7)
  }, [postalCode])
  const onClick = (): void => {
    if (size(postalCode) !== 7) {
      notify('illegal postal code')
      return
    }
    const param = replace(/(\d{3})(\d{4})/, '$1/$2.json')(postalCode as string)
    axios({
      url: `${postalCodeApi}/${param}`,
      method: 'GET'
    })
      .then((result: AxiosResponse<PostalCodeApiResult>) => {
        const { prefecture, address1, address2, address3 } =
          result.data.data[0].ja
        const address = `${prefecture}${address1}${address2}${address3}`
        setValue(field, address)
      })
      .catch(() => {
        notify('illegal postal code')
      })
  }
  return (
    <Button
      {...props}
      variant="contained"
      color="primary"
      disabled={disabled}
      onClick={onClick}>
      {translate('resources.orderR.buttons.addressInput')}
    </Button>
  )
}
const UserNameInput: React.FC<
  InputProps & { fields: string; placeholder: string[] }
> = ({ fields, source, placeholder, ...props }) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const translate = useTranslate()
  const splitedSource = split('.')(source)
  const sourceKey =
    size(splitedSource) < 2 ? source : (last(splitedSource) as string)
  const lastName = `last${replace(/ /g, '')(startCase(sourceKey))}`
  const firstName = `first${replace(/ /g, '')(startCase(sourceKey))}`
  const { setValue, getValues } = useFormContext()
  const onChange =
    (itemSource: string) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const value = event.target.value
      const sourcePrefix = last(split('.')(fields)) as string
      const splitedSource = split('.')(source)
      const values =
        sourcePrefix === 'user'
          ? getValues()
          : // @ts-expect-error ts(2339)
            reduce.convert({ cap: false })(
              (value: any, key: string, index: number) =>
                index === size(splitedSource) - 1 ? value : value[key],
              getValues()
            )(split('.')(source))
      // log.debug({ value, source, values, itemSource })
      if (includes('lastName')(itemSource)) {
        const anotherValue =
          values[
            replace(
              /lastName/,
              'firstName'
            )(last(split('.')(itemSource)) as string)
          ]
        // log.debug({ anotherValue })
        setValue(
          source,
          `${hankana2Zenkana(value)}${hankana2Zenkana(anotherValue as string)}`
        )
      } else {
        const anotherValue =
          values[
            replace(
              /firstName/,
              'lastName'
            )(last(split('.')(itemSource)) as string)
          ]
        // log.debug({ anotherValue })
        setValue(
          source,
          `${hankana2Zenkana(anotherValue as string)}${hankana2Zenkana(value)}`
        )
      }
      setValue(itemSource, hankana2Zenkana(value))
    }
  const { id } = useInput({ source, ...props })
  const lastNameSource =
    sourceKey === source
      ? `${lastName}`
      : `${join('.')(
          slice(0, size(splitedSource) - 1)(splitedSource)
        )}.${lastName}`
  const firstNameSource =
    sourceKey === source
      ? `${firstName}`
      : `${join('.')(
          slice(0, size(splitedSource) - 1)(splitedSource)
        )}.${firstName}`
  // log.debug({
  //   lastNameSource,
  //   firstNameSource,
  //   values: JSON.parse(JSON.stringify(getValues()))
  // })
  return (
    <>
      <Grid container id={id}>
        <Grid item xs={12}>
          <Typography variant="caption">
            {translate(`${fields}.${replace(/\.\d/, '')(source)}`)}
          </Typography>
        </Grid>
        <Grid item xs={isDesktop ? 5.8 : 12}>
          <TextInput
            onChange={onChange(lastNameSource)}
            fields={fields}
            source={lastNameSource}
            placeholder={!isNil(placeholder[0]) ? placeholder[0] : ''}
          />
        </Grid>
        <Grid item xs={isDesktop ? 5.8 : 12}>
          <TextInput
            onChange={onChange(firstNameSource)}
            fields={fields}
            source={firstNameSource}
            placeholder={!isNil(placeholder[1]) ? placeholder[1] : ''}
          />
        </Grid>
      </Grid>
    </>
  )
}
const FormContentUser: React.FC<{
  product: string
  page: number
  config: EntryPartner
  options: any
  schema: any
}> = ({ product, page, config, options, schema }) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const translate = useTranslate()
  const { getValues, setValue } = useFormContext()
  const { anchorEl, setAnchorEl } = options
  const onChange =
    (itemSource: string) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const value = event.target.value as string | boolean
      if (itemSource === 'place')
        setValue(itemSource, hankana2Zenkana(value as string))
      if (itemSource === 'necessaryEKYC' && (value as boolean)) {
        setAnchorEl(event.currentTarget)
      }
    }
  const noEntryDate = (getValues('noEntryDate') as boolean | undefined) ?? false
  const today = Date.now()
  const onChangeNoEntryDateCheck = (
    event: React.ChangeEvent<{ value: boolean }>
  ): void => {
    if (event.target.value) {
      setValue('entry', format('yyyy-MM-dd')(subMonths(1)(today)))
    } else {
      setValue('entry', null)
    }
  }
  const handlePopoverClose = (): void => {
    setAnchorEl(null)
  }
  return (
    <Box
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Box sx={{ width: '1280px' }}>
        <Box>
          <Box sx={{ ml: '0.5em', mr: '0.5em' }}>
            <Grid
              container
              justifyContent="center"
              spacing={2}
              sx={{
                mt: '0.1em'
              }}>
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'center' }}>
                <Typography
                  variant="h4"
                  sx={{
                    mt: '1.5em'
                  }}>
                  {template(translate('resources.orderR.title'))({
                    name: config.product
                  })}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'center' }}>
                <Typography variant="caption">
                  {template(translate(`resources.orderR.header`))({
                    partner: config.partner
                  })}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'center' }}>
                <Typography variant="h5">
                  {translate(`resources.orderR.subtitles.title_${page}`)}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: '-1em'
                }}>
                <picture>
                  <img src="/bar.png" alt="bar" />
                </picture>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: '-1em'
                }}>
                {isNil(config.registPaymentMethod) ||
                !config.registPaymentMethod ? (
                  <Typography variant="h6">
                    <a href={`/manual.pdf`} target="_blank" rel="noreferrer">
                      {translate(`resources.orderR.manual`)}
                    </a>
                  </Typography>
                ) : (
                  <></>
                )}
              </Grid>
              {(isNil(config.noEKYC) || !config.noEKYC) && (
                <>
                  <Grid
                    item
                    xs={12}
                    sx={{ display: 'flex', justifyContent: 'center' }}>
                    <YesNoInput
                      fields="resources.orderR.fields"
                      source="necessaryEKYC"
                      labels={[
                        translate('resources.orderR.yesNo.yes'),
                        translate('resources.orderR.yesNo.no')
                      ]}
                      onChange={onChange('necessaryEKYC')}
                    />
                  </Grid>
                  {
                    <Popover
                      open={!isNil(anchorEl)}
                      anchorEl={anchorEl}
                      onClose={handlePopoverClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center'
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center'
                      }}>
                      <Grid
                        container
                        display={'grid'}
                        justifyContent="center"
                        sx={{
                          backgroundColor: 'Highlight'
                        }}>
                        <Grid
                          item
                          sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              'white-space': 'pre-wrap'
                            }}>
                            {translate('resources.orderR.attention.ekyc.title')}
                          </Typography>
                        </Grid>
                        <Typography
                          variant="body2"
                          sx={{
                            'white-space': 'pre-wrap',
                            m: '1em',
                            mr: '1em'
                          }}>
                          {translate('resources.orderR.attention.ekyc.body')}
                        </Typography>
                        <Grid
                          item
                          sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Button onClick={handlePopoverClose}>
                            {'閉じる'}
                          </Button>
                        </Grid>
                      </Grid>
                    </Popover>
                  }
                </>
              )}
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'center' }}>
                <YesNoInput
                  fields="resources.orderR.fields"
                  source="necessaryContractor"
                  labels={[
                    translate('resources.orderR.yesNo.yes'),
                    translate('resources.orderR.yesNo.no')
                  ]}
                />
              </Grid>
              {!isNil(config.noFamilies) && config.noFamilies && (
                <Grid
                  item
                  xs={12}
                  sx={{ display: 'flex', justifyContent: 'center' }}>
                  <YesNoInput
                    fields="resources.orderR.fields"
                    source="noFamilies"
                    labels={[
                      translate('resources.orderR.yesNo.yes'),
                      translate('resources.orderR.yesNo.no')
                    ]}
                  />
                </Grid>
              )}
              {/* @ts-expect-error ts(2339) */}
              {map.convert({ cap: false })(
                (
                  item: {
                    key: string
                    placeholder: string
                    validate: number
                    description: string
                    url: string
                  },
                  index: number
                ) => {
                  const { key, placeholder, description, url } = item
                  return (
                    <Grid item xs={12} key={`attributes${index}`}>
                      <Grid container>
                        <Grid item xs={isDesktop ? 6 : 12}>
                          <Grid container columnSpacing={2}>
                            <Grid item xs={12}>
                              <TextInput
                                fields="resources.orderR.fields.user"
                                source={`attributes${index}`}
                                label={key}
                                placeholder={placeholder}
                                parse={(value: string) => ({ key, value })}
                                format={(item: {
                                  key: string
                                  value: string
                                }) => (!isNil(item) ? item.value : '')}
                                subsource="value"
                              />
                            </Grid>
                            {!isEmpty(description) && (
                              <Grid
                                item
                                xs={12}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'left',
                                  alignItems: 'flex-end'
                                }}>
                                {!isEmpty(url) ? (
                                  <a href={url} target="_blank">
                                    {map((description: string) => (
                                      <Typography
                                        variant="subtitle1"
                                        sx={{ ml: '2em' }}>
                                        {description}
                                      </Typography>
                                    ))(split('\n')(description))}
                                  </a>
                                ) : (
                                  map((description: string) => (
                                    <Typography
                                      variant="subtitle1"
                                      sx={{ ml: '2em' }}>
                                      {description}
                                    </Typography>
                                  ))(split('\n')(description))
                                )}
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  )
                }
              )(config.attributes)}
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={isDesktop ? 6 : 12}>
                    <UserNameInput
                      fields="resources.orderR.fields.user"
                      source="name"
                      placeholder={['中電', '太郎']}
                    />
                  </Grid>
                  <Grid item xs={isDesktop ? 6 : 12}>
                    <UserNameInput
                      fields="resources.orderR.fields.user"
                      source="nameKana"
                      placeholder={['チュウデン', 'タロウ']}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={isDesktop ? 5.8 : 12}>
                    <TextInput
                      fields="resources.orderR.fields.user"
                      source="postalCode"
                      placeholder="123456"
                    />
                  </Grid>
                  <Grid item xs={isDesktop ? 5.8 : 12}>
                    <FormDataConsumer<EntryUserData>>
                      {({ formData }) => (
                        <AddressInputButton
                          field="address"
                          postalCode={formData.postalCode}
                          sx={{
                            ml: isDesktop ? '1em' : '0.5em',
                            mt: isDesktop ? '1.25em' : '1em',
                            borderRadius: '20px',
                            height: '2.5em'
                          }}
                        />
                      )}
                    </FormDataConsumer>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={11.77}>
                    <TextInput
                      fields="resources.orderR.fields.user"
                      source="address"
                      placeholder="愛知県名古屋市中区栄4-5-3"
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={11.77}>
                    <TextInput
                      fields="resources.orderR.fields.user"
                      source="place"
                      placeholder="KDX名古屋栄ビル"
                      onChange={onChange('place')}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={isDesktop ? 5.8 : 12}>
                    <TextInput
                      fields="resources.orderR.fields.user"
                      source="placeNumber"
                      placeholder="1001"
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={isDesktop ? 5.8 : 12}>
                    <TextInput
                      fields="resources.orderR.fields.user"
                      source="phone"
                      placeholder="09012345678"
                    />
                  </Grid>
                  <Grid
                    item
                    xs={isDesktop ? 5.8 : 12}
                    sx={{ ml: isDesktop ? '1em' : '0em' }}>
                    <TextInput
                      fields="resources.orderR.fields.user"
                      source="email"
                      placeholder="foo@miraiz-connect.co.jp"
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={isDesktop ? 5.8 : 12}>
                    <CountryInput
                      fields="resources.orderR.fields.user"
                      source="country"
                    />
                  </Grid>
                  <Grid
                    item
                    xs={isDesktop ? 5.7 : 12}
                    sx={{ ml: isDesktop ? '1.5em' : '0.5em' }}>
                    <CheckInput
                      fields="resources.orderR.fields"
                      onChange={onChangeNoEntryDateCheck}
                      source="noEntryDate"
                      gridOptions={{
                        display: 'flex',
                        justifyContent: 'left',
                        alignItems: 'center'
                      }}
                    />
                    <Typography variant="body2" whiteSpace="pre-line">
                      {translate(
                        'resources.orderR.fields.entryDateDescription'
                      )}
                    </Typography>
                    {!noEntryDate && (
                      <DateInput
                        fields="resources.orderR.fields.user"
                        source="entry"
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: '1.5em',
                  mb: '2.5em'
                }}>
                <NextButton page={page} product={product} schema={schema} />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
const FormContentContractorAndLessee: React.FC<{
  product: string
  page: number
  config: EntryPartner
  schema: any
}> = ({ product, page, config, schema }) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const translate = useTranslate()
  const { getValues } = useFormContext()
  const { necessaryContractor } = getValues() as EntryUserData
  return (
    <Box
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Box sx={{ width: '1280px' }}>
        <Box>
          <Box sx={{ ml: '0.5em', mr: '0.5em' }}>
            <Grid
              container
              justifyContent="center"
              spacing={2}
              sx={{
                mt: '0.1em'
              }}>
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'center' }}>
                <Typography
                  variant="h4"
                  sx={{
                    mt: '1.5em'
                  }}>
                  {template(translate('resources.orderR.title'))({
                    name: config.product
                  })}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'center' }}>
                <Typography variant="h5">
                  {translate(`resources.orderR.subtitles.title_${page}`)}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: '-1em'
                }}>
                <picture>
                  <img src="/bar.png" alt="bar" />
                </picture>
              </Grid>
              {/* {necessaryEKYC && (
                <>
                  <Grid item xs={12} sx={{ mb: '2em' }}>
                    <Grid container>
                      <Grid item xs={isDesktop ? 6 : 12}>
                        <TextInput
                          fields="resources.orderR.fields.lessee"
                          source="name"
                          placeholder="中電太郎"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              )} */}
              {necessaryContractor && (
                <>
                  <Grid item xs={12}>
                    <Grid container>
                      <Grid item xs={isDesktop ? 6 : 12}>
                        <UserNameInput
                          fields="resources.orderR.fields"
                          source="contractor.name"
                          placeholder={['中電', '次郎']}
                        />
                      </Grid>
                      <Grid item xs={isDesktop ? 6 : 12}>
                        <UserNameInput
                          fields="resources.orderR.fields"
                          source="contractor.nameKana"
                          placeholder={['チュウデン', 'ジロウ']}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container>
                      <Grid item xs={isDesktop ? 5.8 : 12}>
                        <TextInput
                          fields="resources.orderR.fields"
                          source="contractor.postalCode"
                          placeholder="123456"
                        />
                      </Grid>
                      <Grid item xs={isDesktop ? 5.8 : 12}>
                        <FormDataConsumer<EntryUserData>>
                          {({ formData }) =>
                            isNil(formData.contractor) ? (
                              <></>
                            ) : (
                              <AddressInputButton
                                field="contractor.address"
                                postalCode={formData.contractor.postalCode}
                                sx={{
                                  ml: isDesktop ? '1em' : '0.5em',
                                  mt: isDesktop ? '1.25em' : '1em',
                                  borderRadius: '20px',
                                  height: '2.5em'
                                }}
                              />
                            )
                          }
                        </FormDataConsumer>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container>
                      <Grid item xs={11.77}>
                        <TextInput
                          fields="resources.orderR.fields"
                          source="contractor.address"
                          placeholder="愛知県名古屋市中区栄4-5-3"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container>
                      <Grid item xs={isDesktop ? 5.8 : 12}>
                        <TextInput
                          fields="resources.orderR.fields"
                          source="contractor.phone"
                          placeholder="09012345678"
                        />
                      </Grid>
                      <Grid item xs={isDesktop ? 5.8 : 12} sx={{ ml: '1em' }}>
                        <TextInput
                          fields="resources.orderR.fields"
                          source="contractor.email"
                          placeholder="foo@miraiz-connect.co.jp"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              )}
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: '1.5em',
                  mb: '2.5em'
                }}>
                <NextButton page={page} product={product} schema={schema} />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
const FormContentFamilies: React.FC<{
  product: string
  page: number
  config: EntryPartner
  options: any
  schema: any
}> = ({ product, page, config, options, schema }) => {
  const { agreementOpened, setAgreementOpened } = options
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const translate = useTranslate()
  const { getValues, setValue } = useFormContext()
  const { necessaryContractor, noFamilies } = getValues() as EntryUserData & {
    noFamilies: boolean
  }
  const { email, phone, families } = getValues()
  const phoneDuplexCheck =
    !isNil(phone) &&
    !isNil(families) &&
    includes(phone)(
      map((family: { phone?: string | undefined }) => family.phone)(families)
    )
  const emailDuplexCheck =
    !isNil(email) &&
    !isNil(families) &&
    includes(email)(
      map((family: { email?: string | undefined }) => family.email)(families)
    )
  if (
    !isNil(config.necessaryEmergencyEmail) &&
    config.necessaryEmergencyEmail
  ) {
    const extendedSchema = familySchema.extend({
      email: z.string().email('required')
    })
    schema = schema.extend({
      families: z.array(extendedSchema)
    })
  }
  const onClickAgreement = (): void => {
    setAgreementOpened(true)
    if (noFamilies) {
      setValue('families', [
        {
          lastName: ' ',
          firstName: ' ',
          phone: '',
          isAnotherPerson: true
        }
      ])
    }
  }
  return (
    <Box
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Box sx={{ width: '1280px', ml: '0.5em', mr: '0.5em' }}>
        <Grid
          container
          justifyContent="center"
          spacing={2}
          sx={{
            mt: '0.1em'
          }}>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography
              variant="h4"
              sx={{
                mt: '1.5em'
              }}>
              {template(translate('resources.orderR.title'))({
                name: config.product
              })}
            </Typography>
          </Grid>
          {(isNil(noFamilies) || !noFamilies) && (
            <>
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'center' }}>
                <Typography variant="h5">
                  {translate(
                    `resources.orderR.subtitles.title_${
                      !necessaryContractor ? page + 1 : page
                    }`
                  )}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: '-1em'
                }}>
                <picture>
                  <img src="/bar.png" alt="bar" />
                </picture>
              </Grid>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={isDesktop ? 6 : 12}>
                    <UserNameInput
                      fields="resources.orderR.fields"
                      source="families.0.name"
                      placeholder={['中電', '花子']}
                    />
                  </Grid>
                  <Grid item xs={isDesktop ? 6 : 12}>
                    <UserNameInput
                      fields="resources.orderR.fields"
                      source="families.0.nameKana"
                      placeholder={['チュウデン', 'ハナコ']}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={isDesktop ? 5.8 : 12}>
                    <TextInput
                      fields="resources.orderR.fields"
                      source="families.0.postalCode"
                      placeholder="1234567"
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={isDesktop ? 5.8 : 12}>
                    <TextInput
                      fields="resources.orderR.fields"
                      source="families.0.phone"
                      placeholder="09012345678"
                    />
                    {phoneDuplexCheck && (
                      <ValidateError
                        errorMessage={
                          'resources.orderR.fields.contactDuplexCheck'
                        }
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
              {!isNil(config.necessaryEmergencyEmail) &&
              config.necessaryEmergencyEmail ? (
                <Grid item xs={12}>
                  <Grid container>
                    <Grid item xs={isDesktop ? 5.8 : 12}>
                      <TextInput
                        fields="resources.orderR.fields"
                        source="families.0.email"
                        placeholder="foo@miraiz-connect.co.jp"
                      />
                      {emailDuplexCheck && (
                        <ValidateError
                          errorMessage={
                            'resources.orderR.fields.contactDuplexCheck'
                          }
                        />
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              ) : (
                <></>
              )}
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={isDesktop ? 5.8 : 12}>
                    <CountryInput
                      fields="resources.orderR.fields"
                      source="families.0.country"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            {!noFamilies && (
              <CheckInput
                disabled={phoneDuplexCheck || emailDuplexCheck}
                fields="resources.orderR.fields"
                source="families.0.isAnotherPerson"
                gridOptions={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              />
            )}
            <Grid container>
              <Grid item xs={12}>
                <Typography
                  variant="h5"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: '1em'
                  }}>
                  <a
                    href={`/agreement/${config.id}`}
                    target="_blank"
                    onClick={onClickAgreement}
                    rel="noreferrer">
                    {config.agreement}
                  </a>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Grid container justifyContent="center">
                  <Grid item>
                    {!(agreementOpened as boolean) ? (
                      <CheckInput
                        fields="resources.orderR.fields"
                        source="agreementDisabled"
                        disabled
                      />
                    ) : (
                      <CheckInput
                        fields="resources.orderR.fields"
                        source="agreement"
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: '1.5em',
                  mb: '2.5em'
                }}>
                {isNil(config.registPaymentMethod) ||
                !config.registPaymentMethod ? (
                  <ConfirmButton
                    page={page}
                    product={product}
                    schema={schema}
                  />
                ) : (
                  <NextButton page={page} product={product} schema={schema} />
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}
const FormContentPayments: React.FC<{
  product: string
  page: number
  config: EntryPartner
  options: any
  schema: any
}> = ({ product, page, config, schema }) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const translate = useTranslate()
  const { setValue, watch } = useFormContext()
  const paymentMethod = watch('paymentMethod')
  const onChange = (): void => {
    if (disableDirectDebit) {
      setValue('paymentMethod', 'creditCard', {
        shouldDirty: true,
        shouldValidate: true
      })
    }
  }
  return (
    <Box
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Box sx={{ width: '1280px', ml: '0.5em', mr: '0.5em' }}>
        <Grid
          container
          justifyContent="center"
          spacing={2}
          sx={{
            mt: '0.1em'
          }}>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography
              variant="h4"
              sx={{
                mt: '1.5em'
              }}>
              {template(translate('resources.orderR.title'))({
                name: config.product
              })}
            </Typography>
          </Grid>
          {disableDirectDebit ? (
            <></>
          ) : (
            <>
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'center' }}>
                <Typography variant="h5">
                  {translate(`resources.orderR.fields.paymentMethod`)}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: '-1em'
                }}>
                <picture>
                  <img src="/bar.png" alt="bar" />
                </picture>
              </Grid>
              <Grid item xs={isDesktop ? 6 : 11}>
                <RadioInput
                  fields="resources.orderR.fields"
                  source="paymentMethod"
                  labels1={[
                    translate('resources.orderR.fields.payments.creditCard'),
                    translate('resources.orderR.fields.payments.directDebit')
                  ]}
                  values={['creditCard', 'directDebit']}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography variant="h5">
              {translate(`resources.orderR.fields.paymentTerm`)}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: '-1em'
            }}>
            <picture>
              <img src="/bar.png" alt="bar" />
            </picture>
          </Grid>
          <Grid item xs={12} sx={{ ml: isDesktop ? '20em' : '1em' }}>
            <RadioInput
              fields="resources.orderR.fields"
              source="paymentTerm"
              labels1={[
                translate('resources.orderR.fields.payments.monthly'),
                translate('resources.orderR.fields.payments.yearly')
              ]}
              values={['monthly', 'yearly']}
              descriptions2={[
                translate(
                  `resources.orderR.showFields.payments.descriptions.${
                    paymentMethod === 'directDebit'
                      ? 'commission'
                      : 'noCommission'
                  }`
                ),
                translate(
                  'resources.orderR.showFields.payments.descriptions.noCommission'
                )
              ]}
              onChange={onChange}
            />
          </Grid>
          <Grid item xs={12}>
            <Grid container>
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: '1.5em',
                  mb: '2.5em'
                }}>
                <ConfirmButton page={page} product={product} schema={schema} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}
const countryMap = {
  JP: '日本語',
  EN: '英語'
}
const UserTextFieldInternal: React.FC<
  FieldProps & {
    fields: string
    source: string
    type?: string
    ownLabel?: string
  }
> = ({ fields, source, type, ownLabel }) => {
  const translate = useTranslate()
  const { getValues } = useFormContext()
  let value = getValues()
  for (const item of split('.')(source)) {
    value = value[item]
  }
  const convert = (value: any): string =>
    type === 'postalCode'
      ? replace(/(\d{3})(\d{4})/, '$1-$2')(toString(value))
      : type === 'date'
      ? format('yyyy年M月d日')(parseISO(toString(value)))
      : type === 'phone'
      ? !isEmpty(value)
        ? parsePhoneNumber(toString(value), 'JP').formatNational()
        : value
      : type === 'country'
      ? countryMap[toString(value)]
      : type === 'payment'
      ? translate(`resources.orderR.fields.payments.${toString(value)}`)
      : hankana2Zenkana(toString(value))
  return (
    <Labeled
      label={
        !isNil(ownLabel)
          ? ownLabel
          : translate(replace(/\.\d+\./, '.')(`${fields}.${source}`))
      }
      sx={{ fontVariant: 'body1' }}>
      <Typography variant="body1">{convert(value)}</Typography>
    </Labeled>
  )
}
const UserTextField: React.FC<
  FieldProps & {
    fields: string
    source: string
    type?: string
    ownLabel?: string
  }
> = (props) => <UserTextFieldInternal {...props} label={false} />
const UserNameField: React.FC<
  FieldProps & {
    fields: string
    source: string
  }
> = ({ fields, source }) => {
  const translate = useTranslate()
  const { getValues } = useFormContext()
  const sourcePrefix = last(split('.')(fields)) as string
  const splitedSource = split('.')(source)
  const value =
    sourcePrefix === 'user'
      ? getValues()
      : // @ts-expect-error ts(2339)
        reduce.convert({ cap: false })(
          (value: any, key: string, index: number) =>
            index === size(splitedSource) - 1 ? value : value[key],
          getValues()
        )(split('.')(source))
  const sourceKey =
    size(split('.')(source)) < 2 ? source : (last(split('.')(source)) as string)
  const lastName = `last${replace(/ /g, '')(startCase(sourceKey))}`
  const firstName = `first${replace(/ /g, '')(startCase(sourceKey))}`
  // log.debug({ lastName, firstName, value, sourceKey })
  return (
    <Labeled
      label={translate(replace(/\.\d+\./, '.')(`${fields}.${source}`))}
      sx={{ fontVariant: 'body1' }}>
      <Typography variant="body1">{`${hankana2Zenkana(
        value[lastName] as string
      )} ${hankana2Zenkana(value[firstName] as string)}`}</Typography>
    </Labeled>
  )
}
const FormContentConfirm: React.FC<{
  product: string
  page: number
  config: EntryPartner
  schema: any
}> = ({ product, page, config, schema }) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const translate = useTranslate()
  const { getValues } = useFormContext()
  const data = getValues() as EntryUserData & {
    noFamilies: boolean
    noEntryDate: boolean
  }
  // log.debug({ data })
  const {
    necessaryContractor,
    necessaryEKYC,
    noFamilies,
    paymentMethod,
    paymentTerm,
    noEntryDate
  } = data
  const record = { id: '', ...data }
  return (
    <Box
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Box sx={{ width: '1280px', ml: '0.5em', mr: '0.5em' }}>
        <Grid
          container
          justifyContent="center"
          spacing={2}
          sx={{
            mt: '0.1em'
          }}>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography
              variant="h4"
              sx={{
                mt: '1.5em'
              }}>
              {template(translate('resources.orderR.title'))({
                name: config.product
              })}
            </Typography>
          </Grid>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography variant="h5">
              {translate(
                `resources.orderR.subtitles.title_${
                  isNil(config.registPaymentMethod) ||
                  !config.registPaymentMethod
                    ? page + 2
                    : !necessaryContractor || !necessaryEKYC
                    ? page + 1
                    : page
                }`
              )}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: '-1em'
            }}>
            <picture>
              <img src="/bar.png" alt="bar" />
            </picture>
          </Grid>
          <Grid item xs={isDesktop ? 4 : 12}>
            <SimpleShowLayout record={record}>
              {!isNil(config.attributes) &&
                // @ts-expect-error ts(2339)
                map.convert({ cap: false })(
                  (item: { key: string; value: string }, index: number) => {
                    return (
                      <UserTextField
                        source={`attributes${index}.value`}
                        fields="resources.orderR.showFields.user"
                        label={false}
                        ownLabel={item.key}
                        key={`attributes${index}`}
                      />
                    )
                  }
                )(config.attributes)}
              <UserNameField
                fields="resources.orderR.showFields.user"
                source="name"
                label={false}
              />
              <UserNameField
                fields="resources.orderR.showFields.user"
                source="nameKana"
                label={false}
              />
              <UserTextField
                source="postalCode"
                fields="resources.orderR.showFields.user"
                label={false}
                type="postalCode"
              />
              <UserTextField
                source="address"
                fields="resources.orderR.showFields.user"
                label={false}
              />
              <Grid container>
                <Grid item xs={6}>
                  <UserTextField
                    source="place"
                    fields="resources.orderR.showFields.user"
                    label={false}
                  />
                </Grid>
                <Grid item xs={6}>
                  <UserTextField
                    source="placeNumber"
                    fields="resources.orderR.showFields.user"
                    label={false}
                  />
                </Grid>
              </Grid>
              <UserTextField
                source="phone"
                fields="resources.orderR.showFields.user"
                label={false}
                type="phone"
              />
              <UserTextField
                source="email"
                fields="resources.orderR.showFields.user"
                label={false}
              />
              <UserTextField
                source="country"
                fields="resources.orderR.showFields.user"
                label={false}
                type="country"
              />
              {!noEntryDate && (
                <UserTextField
                  source="entry"
                  fields="resources.orderR.showFields.user"
                  label={false}
                  type="date"
                />
              )}
            </SimpleShowLayout>
          </Grid>
          {(necessaryEKYC || necessaryContractor) && (
            <Grid item xs={isDesktop ? 4 : 12}>
              <SimpleShowLayout record={record}>
                {/* {necessaryEKYC && (
                  <UserNameField
                    source="lessee.name"
                    fields="resources.orderR.showFields"
                    label={false}
                  />
                )} */}
                {necessaryContractor && (
                  <SimpleShowLayout record={record}>
                    <UserNameField
                      source="contractor.name"
                      fields="resources.orderR.showFields"
                      label={false}
                    />
                    <UserNameField
                      source="contractor.nameKana"
                      fields="resources.orderR.showFields"
                      label={false}
                    />
                    <UserTextField
                      source="contractor.postalCode"
                      fields="resources.orderR.showFields"
                      label={false}
                      type="postalCode"
                    />
                    <UserTextField
                      source="contractor.address"
                      fields="resources.orderR.showFields"
                      label={false}
                    />
                    <UserTextField
                      source="contractor.phone"
                      fields="resources.orderR.showFields"
                      label={false}
                      type="phone"
                    />
                    <UserTextField
                      source="contractor.email"
                      fields="resources.orderR.showFields"
                      label={false}
                    />
                  </SimpleShowLayout>
                )}
              </SimpleShowLayout>
            </Grid>
          )}
          {(isNil(noFamilies) || !noFamilies) && (
            <Grid item xs={isDesktop ? 4 : 12}>
              <SimpleShowLayout record={record}>
                <UserNameField
                  source="families.0.name"
                  fields="resources.orderR.showFields"
                  label={false}
                />
                <UserNameField
                  source="families.0.nameKana"
                  fields="resources.orderR.showFields"
                  label={false}
                />
                <UserTextField
                  source="families.0.phone"
                  fields="resources.orderR.showFields"
                  label={false}
                  type="phone"
                />
                <UserTextField
                  source="families.0.email"
                  fields="resources.orderR.showFields"
                  label={false}
                  type="email"
                />
                <UserTextField
                  source="families.0.postalCode"
                  fields="resources.orderR.showFields"
                  label={false}
                  type="postalCode"
                />
                <UserTextField
                  source="families.0.country"
                  fields="resources.orderR.showFields"
                  label={false}
                  type="country"
                />
              </SimpleShowLayout>
            </Grid>
          )}
          {!isNil(paymentMethod) && !isNil(paymentTerm) && (
            <Grid
              item
              xs={isDesktop && (necessaryContractor || necessaryEKYC) ? 12 : 4}>
              <SimpleShowLayout record={record}>
                <UserTextField
                  source="paymentMethod"
                  fields="resources.orderR.showFields"
                  label={false}
                  type="payment"
                />
                <UserTextField
                  source="paymentTerm"
                  fields="resources.orderR.showFields"
                  label={false}
                  type="payment"
                />
              </SimpleShowLayout>
            </Grid>
          )}
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: '1.5em',
              mb: '2.5em'
            }}>
            <SubmitButton
              product={product}
              page={page}
              config={config}
              schema={schema}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}
const FormContent: React.FC<{
  product: string
  page: number
  config: EntryPartner
  attributeSchemas: any[]
}> = ({ product, page, config, attributeSchemas }) => {
  const [agreementOpened, setAgreementOpened] = React.useState(false)
  const [anchorEl, setAnchorEl] = React.useState<
    (EventTarget & HTMLInputElement) | null
  >(null)
  const necessaryContractor = useWatch({
    name: 'necessaryContractor'
  }) as boolean
  const noFamilies = useWatch({ name: 'noFamilies' }) as boolean
  const formContents = !necessaryContractor
    ? [FormContentUser, FormContentFamilies, FormContentConfirm]
    : [
        FormContentUser,
        FormContentContractorAndLessee,
        FormContentFamilies,
        FormContentConfirm
      ]

  const schema: any[] = !necessaryContractor
    ? noFamilies
      ? userSchemasWithoutContractorNoFamily
      : userSchemasWithoutContractor
    : noFamilies
    ? userSchemasWithContractorNoFamily
    : userSchemasWithContractor

  if (!isNil(config.registPaymentMethod) && config.registPaymentMethod) {
    formContents.splice(size(formContents) - 1, 0, FormContentPayments)
    schema.splice(size(schema) - 1, 0, paymentSchema)
  }
  const content = formContents[page]
  // const { attributes } = config
  // // @ts-expect-error ts(2339)
  // const validations = map.convert({ cap: false })(
  //   (
  //     item: {
  //       key: string
  //       placeholder: string
  //       validate: number
  //       description: string
  //       url: string
  //     },
  //     index: number
  //   ) => {
  //     const { validate } = item
  //     return { index, validate }
  //   }
  // )(attributes)
  // for (let i = 0; i < size(schema); i++) {
  //   let userSchema: any = schema[i]
  //   if (!isEmpty(validations) && !isNil(userSchema)) {
  //     for (const validation of validations) {
  //       const { index, validate } = validation
  //       userSchema = userSchema?.extend({
  //         [`attributes${index as number}`]: z.object({
  //           key: z.string(),
  //           value: z
  //             .string()
  //             .length(validate, { message: `length is:${validate}` })
  //         })
  //       })
  //     }
  //     schema[i] = userSchema
  //   }
  // }
  for (const attributeSchema of attributeSchemas) {
    schema[page] = schema[page]?.extend(attributeSchema)
  }
  if (isNil(content)) return <></>
  return content({
    product,
    page,
    config,
    options: { agreementOpened, setAgreementOpened, anchorEl, setAnchorEl },
    schema: schema[page]
  })
}
export const Footer: React.FC<{ noFooter?: boolean }> = ({ noFooter }) => {
  return !isNil(noFooter) && noFooter ? (
    <></>
  ) : (
    <footer>
      <div className="footerwrapper_3">
        <div className="footerInner">
          <ul className="linkUl">
            <li>
              <a href="https://miraiz-connect.co.jp/" target="_blank">
                運営会社
              </a>
            </li>
            <li>
              <a
                href="https://miraiz-connect.co.jp/privacy/website/"
                target="_blank">
                プライバシーポリシー
              </a>
            </li>
          </ul>
          <div className="info_box">
            <p className="logo">
              <img src="/common/img/logo.png" alt="中部電力ミライズコネクト" />
            </p>
            <div className="info">
              <p className="adr">愛知県名古屋市中区栄4丁目5番3号</p>
              <p className="ask"></p>
            </div>
          </div>
        </div>
      </div>
      <center>
        <p id="copy">
          (C)Chubu Electric Power Miraiz Connect, <br className="sp" />
          Inc.All Rights Reserved
        </p>
      </center>
    </footer>
  )
}
const Header: React.FC = () => {
  return (
    <div id="headerBlockBg">
      <div id="headerBlock">
        <header>
          <h1 className="siteLogo">
            <img src="/common/img/siteLogo.svg" alt="テラシテR" />
          </h1>
        </header>
      </div>
    </div>
  )
}

export const OrderFormR: React.FC<{
  setProduct: (product: string) => void
}> = ({ setProduct }) => {
  const { product, page: pageParam } = useParams()
  if (!isNil(product)) setProduct(product)
  const [queryParams] = useSearchParams()
  const id = queryParams.get('id')
  const page = toNumber(pageParam)
  const { data: config, isLoading } = useGetOne<EntryPartner>('partners', {
    id: product as string
  })
  const dataProvider = useDataProvider()
  const [values, setValues] = React.useState<
    Partial<EntryUserData & { id?: string }> | undefined
  >(formDefaultValue)
  React.useEffect(() => {
    if (isLoading || isNil(config)) return
    if (!isNil(id)) {
      dataProvider
        .getOne<EntryUserData & { id: string }>('users', { id })
        .then(({ data }) => {
          setValues(data)
        })
        .catch(log.error)
    }
    document.title = config.product
  }, [isLoading, config, id])
  if (isLoading) return <Loading />
  if (isNil(product) || isNil(config)) {
    return <></>
  }
  const { attributes: attributesValues } = values ?? { attributes: [] }
  const defaultValues = {
    necessaryEKYC: !isNil(config.noEKYC) && config.noEKYC ? false : undefined,
    ...values,
    necessaryEmail: true, //! config.orderByUser,
    service: config.product,
    // @ts-expect-error ts(2339)
    ...reduce.convert({ cap: false })(
      (sum: any, item: { key: string; value: string }, index: number) => ({
        ...sum,
        [`attributes${index}`]: item
      }),
      {}
    )(attributesValues)
  }
  const { attributes, noFamilies } = config
  assign(defaultValues, { noFamilies })
  // @ts-expect-error ts(2339)
  const validations = map.convert({ cap: false })(
    (
      item: {
        key: string
        placeholder: string
        validate: number
        description: string
        url: string
      },
      index: number
    ) => {
      const { validate } = item
      return { index, validate }
    }
  )(attributes)
  let schema: any = userSchemaAll
  const attributeSchemas: any[] = []
  if (!isEmpty(validations)) {
    for (const validation of validations) {
      const { index, validate } = validation
      const attributeSchema = {
        [`attributes${index as number}`]: z.object({
          key: z.string(),
          value: z
            .string()
            .length(validate, { message: `length is:${validate as number}` })
        })
      }
      schema = schema.extend(attributeSchema)
      attributeSchemas.push(attributeSchema)
    }
  }
  return (
    <>
      <Header />
      <Box sx={{ mt: '1em' }}>
        <Form
          resolver={zodResolver(schema)}
          mode="onChange"
          reValidateMode="onChange"
          defaultValues={defaultValues}>
          <FormContent
            product={product}
            page={page}
            config={config}
            attributeSchemas={attributeSchemas}
          />
        </Form>
      </Box>
      <Footer noFooter={isNil(config.noFooter) ? false : config.noFooter} />
    </>
  )
}
export const OrderCompleteFormR: React.FC = () => {
  const translate = useTranslate()
  const { product, id } = useParams()
  const { data: config, isLoading: isLoadingPartner } = useGetOne<EntryPartner>(
    'partners',
    {
      id: product as string
    }
  )
  const { data: user, isLoading: isLoadingUser } = useGetOne<
    EntryUserData & { id: string }
  >('users', {
    id: id as string
  })
  React.useEffect(() => {
    if (isLoadingPartner || isNil(config)) return
    document.title = config.product
  }, [isLoadingPartner, config])
  if (isLoadingPartner || isLoadingUser) return <Loading />
  if (isNil(product) || isNil(config) || isNil(user)) {
    return <></>
  }
  const { necessaryContractor, necessaryEKYC, sign } = user
  return (
    <>
      <Header />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: '2em'
        }}>
        <Box sx={{ width: '1280px', ml: '0.5em', mr: '0.5em' }}>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography
              variant="h4"
              sx={{
                mt: '1.5em'
              }}>
              {template(translate('resources.orderR.title'))({
                name: config.product
              })}
            </Typography>
          </Grid>
          <Grid
            container
            justifyContent="center"
            spacing={2}
            sx={{
              mt: '3em'
            }}>
            {/* @ts-expect-error ts(2339)  */}
            {map.convert({ cap: false })((item: string, index: number) => (
              <Grid
                item
                xs={8}
                sx={{ display: 'flex', mt: '0.01em', justifyContent: 'center' }}
                key={index}>
                <Typography
                  variant="h6"
                  sx={{
                    mt: '0em'
                  }}>
                  {item}
                </Typography>
              </Grid>
            ))(
              split('\n')(
                translate(
                  isNil(sign) ||
                    necessaryEKYC ||
                    (!isNil(config.registPaymentMethod) &&
                      config.registPaymentMethod)
                    ? 'resources.orderR.complete.thanks'
                    : 'resources.orderR.complete.thanksComplete'
                )
              )
            )}
          </Grid>
          {(isNil(sign) ||
            necessaryEKYC ||
            (!isNil(config.registPaymentMethod) &&
              config.registPaymentMethod)) && (
            <Grid
              container
              justifyContent="center"
              spacing={2}
              sx={{
                mt: '3em'
              }}>
              {/* @ts-expect-error ts(2339)  */}
              {map.convert({ cap: false })((item: string, index: number) => (
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    mt: '0.01em',
                    justifyContent: 'center'
                  }}
                  key={index}>
                  <Typography
                    variant="h6"
                    sx={{
                      mt: '0em'
                    }}>
                    {item}
                  </Typography>
                </Grid>
              ))(
                split('\n')(
                  template(
                    necessaryEKYC
                      ? necessaryContractor
                        ? translate(
                            'resources.orderR.complete.sendToContractorForBoth'
                          )
                        : config.orderByUser
                        ? translate('resources.orderR.complete.doBoth')
                        : translate('resources.orderR.complete.sendForBoth')
                      : necessaryContractor
                      ? translate(
                          'resources.orderR.complete.sendToContractorForSign'
                        )
                      : config.orderByUser
                      ? translate('resources.orderR.complete.doSign')
                      : translate('resources.orderR.complete.sendForSign')
                  )({ registPaymentMethod: config.registPaymentMethod })
                )
              )}
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: '3em',
                  mb: '2.5em'
                }}>
                {necessaryEKYC ? (
                  necessaryContractor ? (
                    <></>
                  ) : config.orderByUser ? (
                    <EKYCButton user={user} />
                  ) : (
                    <></>
                  )
                ) : (
                  <></>
                )}
              </Grid>
            </Grid>
          )}
        </Box>
      </Box>
      <Footer noFooter={isNil(config.noFooter) ? false : config.noFooter} />
    </>
  )
}
export const EkycForm: React.FC = () => {
  const translate = useTranslate()
  const { id } = useParams()
  const { data: user, isLoading: isLoadingUser } = useGetOne<
    EntryUserData & { id: string } & { config: EntryPartner }
  >('users', {
    id: id as string
  })
  React.useEffect(() => {
    if (isLoadingUser || isNil(user)) return
    document.title = user.service
  }, [isLoadingUser, user])
  if (isLoadingUser) return <Loading />
  if (isNil(user)) {
    return <></>
  }
  const { necessaryEKYC, config } = user
  if (!necessaryEKYC) return <NotFound />
  return (
    <>
      <Header />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: '2em'
        }}>
        <Box sx={{ width: '1280px', ml: '0.5em', mr: '0.5em' }}>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography
              variant="h5"
              sx={{
                mt: '2.5em'
              }}>
              {template(translate('resources.ekyc.title'))({
                name: user.service
              })}
            </Typography>
          </Grid>
          <Grid
            container
            justifyContent="center"
            spacing={2}
            sx={{
              mt: '3em'
            }}>
            {/* @ts-expect-error ts(2339)  */}
            {map.convert({ cap: false })((item: string, index: number) => (
              <Grid
                item
                xs={8}
                sx={{ display: 'flex', mt: '0.01em' }}
                key={index}>
                <Typography
                  variant="h6"
                  sx={{
                    mt: '0em'
                  }}>
                  {item}
                </Typography>
              </Grid>
            ))(
              split('\n')(
                template(translate('resources.ekyc.thanks'))({
                  ...user
                })
              )
            )}
          </Grid>
          <Grid
            container
            justifyContent="center"
            spacing={2}
            sx={{
              mt: '0.1em'
            }}>
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: '3em',
                mb: '2.5em'
              }}>
              <EKYCButton user={user} />
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Footer
        noFooter={
          isNil(config)
            ? true
            : isNil(config.noFooter)
            ? false
            : config.noFooter
        }
      />
    </>
  )
}
export const SignForm: React.FC<{ direct?: boolean }> = ({ direct }) => {
  const translate = useTranslate()
  const { id } = useParams()
  const { data: user, isLoading: isLoadingUser } = useGetOne<
    EntryUserData & { id: string } & { config?: EntryPartner }
  >('users', {
    id: id as string
  })
  React.useEffect(() => {
    if (isLoadingUser || isNil(user)) return
    document.title = user.service
  }, [isLoadingUser, user])
  if (isLoadingUser) return <Loading />
  if (isNil(user)) {
    return <></>
  }
  const { necessaryContractor, config } = user
  return (
    <>
      <Header />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: '2em'
        }}>
        <Box sx={{ width: '1280px', ml: '0.5em', mr: '0.5em' }}>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography
              variant="h5"
              sx={{
                mt: '2.5em'
              }}>
              {template(translate('resources.sign.title'))({
                name: user.service
              })}
            </Typography>
          </Grid>
          <Grid
            container
            justifyContent="center"
            spacing={2}
            sx={{
              mt: '3em'
            }}>
            {(isNil(direct) || !direct) && (
              <Grid item xs={8} sx={{ display: 'flex', mt: '0.01em' }}>
                <Typography
                  variant="h6"
                  sx={{
                    mt: '0em'
                  }}>
                  {translate('resources.sign.thanks.ekyc')}
                </Typography>
              </Grid>
            )}
            {/* @ts-expect-error ts(2339) */}
            {map.convert({ cap: false })((item: string, index: number) => (
              <Grid
                item
                xs={8}
                sx={{ display: 'flex', mt: '0.01em' }}
                key={index}>
                <Typography
                  variant="h6"
                  sx={{
                    mt: '0em'
                  }}>
                  {item}
                </Typography>
              </Grid>
            ))(
              split('\n')(
                template(
                  translate(
                    necessaryContractor
                      ? 'resources.sign.thanks.contractor'
                      : 'resources.sign.thanks.user'
                  )
                )({
                  ...user
                })
              )
            )}
          </Grid>
        </Box>
      </Box>
      <Footer
        noFooter={
          isNil(config)
            ? true
            : isNil(config.noFooter)
            ? false
            : config.noFooter
        }
      />{' '}
    </>
  )
}
export const SignFormWithoutUser: React.FC = () => {
  const translate = useTranslate()
  return (
    <>
      <Header />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: '2em'
        }}>
        <Box sx={{ width: '1280px', ml: '0.5em', mr: '0.5em' }}>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography
              variant="h5"
              sx={{
                mt: '1.5em'
              }}>
              {translate('resources.sign.withoutUser.title')}
            </Typography>
          </Grid>
          <Grid
            container
            justifyContent="center"
            spacing={2}
            sx={{
              mt: '3em'
            }}>
            <Grid item xs={8} sx={{ display: 'flex', mt: '0.01em' }}>
              <Typography
                variant="h6"
                sx={{
                  mt: '0em'
                }}>
                {translate('resources.sign.thanks.ekyc')}
              </Typography>
            </Grid>
            {map((item: string) => (
              <Grid item xs={8} sx={{ display: 'flex', mt: '0.01em' }}>
                <Typography
                  variant="h6"
                  sx={{
                    mt: '0em'
                  }}>
                  {item}
                </Typography>
              </Grid>
            ))(split('\n')(translate('resources.sign.withoutUser.thanks')))}
          </Grid>
        </Box>
      </Box>
    </>
  )
}
export const SelectProductForm: React.FC = () => {
  const { product } = useParams()
  const urProduct = {
    prd: '170f2f21-1e6b-4871-b38c-6fb527a67ba5',
    stg: '',
    int: '',
    exp: '33902793-86e3-41c5-863f-6e142be347f4',
    dev: ''
  }[env] as string
  if (isNil(product)) {
    return <></>
  }
  if (product === urProduct) {
    return <SelectProductFormUr />
  }
  return <></>
}
export const SelectProductFormUr: React.FC = () => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const redirect = useRedirect()
  const [enableButton, setEnableButton] = useState<boolean>(false)
  const [radioValue, setRadioValue] = useState<string | undefined>(undefined)
  const translate = useTranslate()
  const domain = !isNil(local)
    ? `localhost:5173`
    : ({
        prd: `entry.terashite.jp`,
        stg: `entry-stg.terashite.jp`,
        int: `entry-int.dev.terashite.jp`,
        exp: `entry-exp.dev.terashite.jp`,
        dev: `entry.bapp.dev.terashite.jp`
      }[env] as string)
  const labels1 = [
    '①テラシテR',
    isDesktop ? '②テラシテR 在宅確認サポート' : '②テラシテR\n在宅確認サポート',
    isDesktop
      ? '③テラシテR あんしん生活サービス'
      : '③テラシテR\nあんしん生活サービス',
    isDesktop
      ? '④テラシテR 在宅確認サポート＋あんしん生活サービス'
      : '④テラシテR\n在宅確認サポート＋\nあんしん生活サービス'
  ]
  const labels2 = ['\t※1', '\t※2', '\t※3', '']
  const descriptions1 = [
    '月額990円（税込）',
    '月額1,320円（税込）',
    '月額1,320円（税込）',
    '月額1,650円（税込）'
  ]
  const descriptions2 = [
    '電気の使用量の変化をとらえて入居者さまを見守ります。異変を検知した際は、入居者さまや緊急連絡先さまに通知いたします。',
    '緊急連絡先さまに代わり、入居者さまの在宅確認を行うプランです。',
    '災害時安否共有や生活の様々な相談ができるプランです。',
    '②と③がセットになったプランです。'
  ]
  const attentions =
    '※1　あらかじめご登録いただく入居者さまおよび緊急連絡先さまの携帯番号または固定電話に通知します。\n※2　テラシテR在宅確認サポート：緊急連絡先さまからの依頼により、安否確認通知が届いた際に入居者さまの在宅確認を月2回まで行います。インターフォン、メーター確認、電話、ポスト確認、ベランダ等の外観確認を行い、確認内容をご報告します。なお、警備業法に規定されている警備業務は一切行うことはできません。3回目以降は、8,800円(税込)/回がかかります。\n※3　テラシテRあんしん生活サービス：災害発生時、安否確認の連絡を行い、回答内容を緊急連絡先さまへ報告致します。入居者さまからの健康・医療・介護・暮らし等に関する相談やトラブル解決に向けた情報提供・紹介を行います。健康･医療、介護相談は24時間対応（※）で看護師等の専門職が応答します。暮らし等の相談は平日14時～17時、情報提供・紹介サービスは平日10時～17時で対応しております。個人賠償責任保険（1億円）、傷害死亡保険（10万円）がついています。\n※サービス対象範囲の詳細、免責事項については、UR都市機構入居者さま向けテラシテR利用規約をご確認ください。'
  const product1 = {
    prd: '170f2f21-1e6b-4871-b38c-6fb527a67ba5-0',
    stg: '',
    int: '',
    dev: '',
    exp: '33902793-86e3-41c5-863f-6e142be347f4-12'
  }[env] as string
  const product2 = {
    prd: '170f2f21-1e6b-4871-b38c-6fb527a67ba5-4',
    stg: '',
    int: '',
    dev: '',
    exp: '33902793-86e3-41c5-863f-6e142be347f4-10'
  }[env] as string
  const product3 = {
    prd: '170f2f21-1e6b-4871-b38c-6fb527a67ba5-9',
    stg: '',
    int: '',
    dev: '',
    exp: '33902793-86e3-41c5-863f-6e142be347f4-11'
  }[env] as string
  const product4 = {
    prd: '170f2f21-1e6b-4871-b38c-6fb527a67ba5-14',
    stg: '',
    int: '',
    dev: '',
    exp: '33902793-86e3-41c5-863f-6e142be347f4-9'
  }[env] as string
  const redirectUrls = [
    `https://${domain}/${product1}`,
    `https://${domain}/${product2}`,
    `https://${domain}/${product3}`,
    `https://${domain}/${product4}`
  ]
  const onClickButton = (): void => {
    setEnableButton(false)
    if (!isNil(radioValue)) {
      redirect(radioValue)
    }
  }
  const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value
    setRadioValue(value)
    setEnableButton(true)
  }
  return (
    <>
      <Header />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: '2em'
        }}>
        <Box sx={{ width: '1280px', ml: '0.5em', mr: '0.5em' }}>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography
              variant="h4"
              sx={{
                mt: '1.5em'
              }}>
              {template(translate('resources.orderR.title'))({
                name: 'テラシテR'
              })}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{ mt: '1em', display: 'flex', justifyContent: 'center' }}>
            <Typography variant="caption">
              {
                '私(電気契約者)は、テラシテRサービス利用規約、注意事項および電力使用状況の異変検知時に緊急連絡先へ個人情報を提供することについて、賃借人、入居者、緊急連絡先にテラシテRの内容を説明し、賃借人、入居者、緊急連絡先から同意を得ていることを誓約のうえ申込みをします。'
              }
            </Typography>
          </Grid>
          <Grid
            container
            justifyContent="center"
            spacing={2}
            sx={{
              mt: '0.5em'
            }}>
            <Grid
              item
              xs={12}
              sx={{ display: 'flex', justifyContent: 'center' }}>
              <Typography variant="h5">{`プランの選択`}</Typography>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: '-1em'
              }}>
              <picture>
                <img src="/bar.png" alt="bar" />
              </picture>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                justifyContent: 'center'
              }}>
              <Typography
                variant="body2"
                sx={{
                  'white-space': 'pre-wrap'
                }}>
                {'ご希望のプランを選択してください。'}
              </Typography>
            </Grid>
            <Grid
              container
              xs={isDesktop ? 6 : 11}
              sx={{
                display: 'grid',
                justifyContent: 'normal',
                alignItems: 'center'
              }}>
              {
                // @ts-expect-error ts(2339)
                map.convert({ cap: false })((label: string, index: number) => {
                  return (
                    <Grid
                      item
                      marginY={2}
                      sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Radio
                            checked={
                              !isEmpty(radioValue) &&
                              radioValue === redirectUrls[index]
                            }
                            checkedIcon={<CheckBoxIcon />}
                            icon={<CheckBoxOutlineBlankIcon />}
                            onChange={onChangeRadio}
                            value={redirectUrls[index]}
                            sx={{}}
                          />
                        }
                        label=""
                        sx={{
                          whiteSpace: 'pre-wrap',
                          mb: '-0.5em'
                        }}
                      />
                      <Grid>
                        <Grid container display="flex">
                          <Typography
                            variant="body1"
                            sx={{ 'white-space': 'pre-wrap' }}>
                            {labels1[index]}
                          </Typography>
                          <Grid
                            item
                            alignContent="end"
                            sx={{
                              ml: '-1.5em'
                            }}>
                            <Typography
                              variant="body2"
                              sx={{
                                'white-space': 'pre-wrap',
                                fontStyle: 'italic'
                              }}>
                              {labels2[index]}
                            </Typography>
                          </Grid>
                        </Grid>
                        {!isNil(descriptions1) ? (
                          <Typography
                            variant="body1"
                            sx={{
                              mt: '0.5em',
                              mr: '0.5em',
                              'white-space': 'pre-wrap',
                              fontSize: 15
                            }}>
                            {descriptions1[index]}
                          </Typography>
                        ) : (
                          <></>
                        )}
                        {!isNil(descriptions2) ? (
                          <Typography
                            variant="body2"
                            sx={{
                              mt: '0.5em',
                              mr: '0.5em',
                              'white-space': 'pre-wrap'
                            }}>
                            {descriptions2[index]}
                          </Typography>
                        ) : (
                          <></>
                        )}
                      </Grid>
                    </Grid>
                  )
                })(labels1)
              }
              <Grid
                item
                sx={{
                  mt: '2em',
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                <Typography
                  variant="body2"
                  sx={{ 'white-space': 'pre-wrap', fontSize: 13 }}>
                  {attentions}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          <Grid
            container
            justifyContent="center"
            spacing={2}
            sx={{
              mt: '0.1em'
            }}>
            <Grid item xs={4} sx={{ ml: '0.5em' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                sx={{ borderRadius: '10px' }}
                disabled={!enableButton}
                fullWidth
                onClick={onClickButton}>
                <Typography variant="button">
                  {translate('resources.orderR.buttons.next')}
                </Typography>
              </Button>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: '3em',
                mb: '2.5em'
              }}></Grid>
          </Grid>
        </Box>
      </Box>
      <Footer />
    </>
  )
}
export const GmoResult: React.FC = () => {
  const { result } = useParams()
  if (isNil(result)) return <></>
  return (
    <>
      <Header />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: '2em'
        }}>
        <Box sx={{ width: '1280px', ml: '0.5em', mr: '0.5em' }}>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography
              variant="h5"
              sx={{
                mt: '2.5em'
              }}>
              {`お支払い方法のご登録`}
            </Typography>
          </Grid>
          <Grid
            container
            justifyContent="center"
            spacing={2}
            marginBottom={2}
            sx={{
              mt: '3em'
            }}>
            <Grid
              item
              xs={12}
              sx={{ display: 'flex', justifyContent: 'center' }}>
              <Typography variant="h5">
                {result === 'success' ? `登録成功` : `登録失敗`}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: '-1em'
              }}>
              <picture>
                <img src="/bar.png" alt="bar" />
              </picture>
            </Grid>
          </Grid>
          <Grid
            xs={12}
            sx={{
              display: 'flex',
              justifyContent: 'center'
            }}>
            <Typography
              variant="body1"
              sx={{ mx: '1em', 'white-space': 'pre-wrap' }}>
              {result === 'success'
                ? `お支払い方法の登録に成功しました。画面を閉じてください。`
                : `お支払い方法の登録に失敗しました。大変お手数ですが、もう一度やりなおしてください。`}
            </Typography>
          </Grid>
          <Grid
            container
            justifyContent="center"
            spacing={2}
            sx={{
              mt: '0.1em'
            }}>
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: '3em',
                mb: '2.5em'
              }}></Grid>
          </Grid>
        </Box>
      </Box>
      <Footer />
    </>
  )
}
